import { MemberUtils } from "@shared/member.utils";
import { useSearchParams } from "react-router-dom";
import { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import { BackButton } from "../components/common/BackButton";
import { MemberShipCard } from "../components/shared/MembershipCard";
import { useSeasonStore } from "../stores/season.store";
import { useProfileOwner, useUserStore } from "../stores/user.store";
import CarouselWrapper, {
  CarouselRef,
} from "../components/common/CarouselWrapper";
import { Button } from "../components/common/Button";
import { downloadCard } from "../utils/cardGenerator";

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const CardContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
  min-height: 0;

  @media (max-width: 768px) {
    overflow: visible;
  }
`;

const ShareButtonContainer = styled.div`
  width: 100%;
  padding: 10px 20px;
  background: white;
  display: flex;
  justify-content: center;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 8px 10px;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #666;
  font-size: 18px;
  padding: 40px;
`;

const SeasonName = styled.h1`
  margin: 16px 20px 0;
  text-align: center;
  font-size: 20px;
`;

export const CardDisplay = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile } = useUserStore();
  const profileOwner = useProfileOwner();
  const { latestSeason } = useSeasonStore();
  const carouselRef = useRef<CarouselRef>(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);

  // Get member ID from URL params
  const memberId = searchParams.get("memberId");

  // Effect for setting initial slide - must be before any conditional returns
  useEffect(() => {
    if (!profile || !profileOwner || !latestSeason) {
      return;
    }

    const membersWithSubs = profile.members.filter((member) => {
      const subscription = MemberUtils.getValidSubscription(
        member,
        latestSeason,
      );
      return subscription !== null;
    });

    if (memberId && membersWithSubs.length > 0) {
      const foundIndex = membersWithSubs.findIndex((m) => m.id === memberId);
      if (foundIndex !== -1 && foundIndex !== currentSlideIndex) {
        setCurrentSlideIndex(foundIndex);
      }
    }
  }, [memberId, profile, profileOwner, latestSeason]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!profile || !profileOwner || !latestSeason) {
    return (
      <PageContainer>
        <div style={{ padding: "20px" }}>
          <BackButton />
        </div>
        <LoadingMessage>
          Crea la sottoscrizione annuale per poter vedere la tua tessera.
        </LoadingMessage>
      </PageContainer>
    );
  }

  // Filter all family members with valid subscriptions
  const membersWithSubscriptions = profile.members.filter((member) => {
    const subscription = MemberUtils.getValidSubscription(
      member,
      latestSeason,
    );
    return subscription !== null;
  });

  // Determine initial slide index based on memberId parameter
  let initialSlideIndex = 0;
  if (memberId) {
    const foundMemberIndex = membersWithSubscriptions.findIndex(
      (m) => m.id === memberId,
    );
    if (foundMemberIndex !== -1) {
      initialSlideIndex = foundMemberIndex;
    }
  }

  // Handle slide changes to update URL
  const handleSlideChange = (currentSlide: number) => {
    setCurrentSlideIndex(currentSlide);
    const currentMember = membersWithSubscriptions[currentSlide];
    if (currentMember) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("memberId", currentMember.id);
      setSearchParams(newSearchParams, { replace: true });
    }
  };

  // Handle download for current slide
  const handleDownload = async () => {
    const currentMember = membersWithSubscriptions[currentSlideIndex];
    if (currentMember) {
      setIsDownloading(true);
      try {
        const userName = `${currentMember.first_name} ${currentMember.last_name}`;
        const filename = `co2-membership-card-${userName
          .replace(/\s+/g, "-")
          .toLowerCase()}.png`;
        await downloadCard(
          { userName },
          latestSeason.image_url || "",
          filename,
        );
      } catch (error) {
        console.error("Error sharing card:", error);
        alert("Error sharing card. Please try again.");
      } finally {
        setIsDownloading(false);
      }
    }
  };

  // Create member names array for display
  const memberNames = membersWithSubscriptions.map(
    (member) => `${member.first_name} ${member.last_name}`,
  );

  // Create card components for each member
  const cardComponents = membersWithSubscriptions.map((member) => {
    const userName = `${member.first_name} ${member.last_name}`;
    return (
      <MemberShipCard
        key={member.id}
        userName={userName}
        backgroundImageUrl={latestSeason.image_url || ""}
        className="card-display-large"
      />
    );
  });

  return (
    <PageContainer>
      <BackButton />
      <SeasonName>TESSERA {latestSeason.name}</SeasonName>
      <CardContainer>
        <CarouselWrapper
          ref={carouselRef}
          initialSlide={initialSlideIndex}
          memberNames={memberNames}
          onSlideChange={handleSlideChange}
        >
          {cardComponents}
        </CarouselWrapper>
      </CardContainer>
      <ShareButtonContainer>
        <Button
          onClick={handleDownload}
          disabled={isDownloading}
          variant="outline"
          style={{
            width: "100%",
            maxWidth: "300px",
            padding: "16px",
            fontSize: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px",
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
          {isDownloading ? "Condivisione..." : "condividi"}
        </Button>
      </ShareButtonContainer>
    </PageContainer>
  );
};
